import { makeStyles } from '@material-ui/core/styles';

import { Box, Card, CardBody, Flex, Text } from '@backstage/ui';

import {
  extractLastNameComponent,
  pathToModuleStyle,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';

import {
  DEFAULT_ARTIFACT_ICON,
  useArtifactExtensions,
} from '../../extensions/artifact-extension';

const useStyles = makeStyles({
  cardButton: {
    background: 'none',
    border: 0,
    cursor: 'pointer',
    display: 'block',
    height: '100%',
    padding: 0,
    textAlign: 'inherit',
    width: '100%',
    '&:hover $card': {
      backgroundColor: 'var(--bui-bg-neutral-3)',
    },
    '&:focus-visible $card': {
      outline: '2px solid var(--bui-ring)',
      outlineOffset: '2px',
    },
  },
  card: {
    backgroundColor: 'var(--bui-bg-neutral-2)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    transition: 'background-color 0.15s ease',
  },
  iconContainer: {
    alignItems: 'center',
    borderRadius: 'var(--bui-radius-2)',
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    paddingRight: 2,
    width: 32,
    '& > svg': {
      height: 16,
      width: 16,
    },
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
});

interface DefaultArtifactCardProps {
  artifact: AttachmentFragment;
  resolvedDisplayName?: string;
  onClick: () => void;
}

export const DefaultArtifactCard = ({
  artifact,
  resolvedDisplayName,
  onClick,
}: DefaultArtifactCardProps) => {
  const classes = useStyles();
  const extensions = useArtifactExtensions();
  const ext = extensions.find(e => e.predicate(artifact));

  const { Icon, color } = ext?.icon?.(artifact) ?? DEFAULT_ARTIFACT_ICON;
  const typeLabel =
    pathToModuleStyle(artifact.uri)?.label ?? ext?.label ?? 'Link';
  const fallbackName = extractLastNameComponent(artifact.uri);

  return (
    <button type="button" className={classes.cardButton} onClick={onClick}>
      <Card className={classes.card}>
        <CardBody>
          <Flex align="center" gap="2">
            <Box
              className={classes.iconContainer}
              style={{
                backgroundColor: `color-mix(in srgb, ${color}, transparent 80%)`,
              }}
            >
              <Icon size={16} style={{ color }} />
            </Box>
            <Flex className={classes.info} direction="column" gap="0">
              <Text variant="body-medium" weight="bold" truncate>
                {artifact.displayName || resolvedDisplayName || fallbackName}
              </Text>
              <Text variant="body-small" color="secondary" truncate>
                {typeLabel}
              </Text>
            </Flex>
          </Flex>
        </CardBody>
      </Card>
    </button>
  );
};
